import React from "react";
import {
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DroppableStateSnapshot,
} from "react-beautiful-dnd";
import styled from "styled-components";
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from "@mui/material";

import { ControlType, RegistrationData, UIControlRegistration } from "../Utilities/RegisterUIControl";
import { FormDesignConstants, FormMode } from "../Utilities/FormDesignConstants";
import { ExpandIcon } from "../../CommonUI/Icon/ExpandIcon";
import { UIButton } from "../../CommonUI/BuildlingBlocks/UIButton";
import { Box } from "@mui/material";
import { UILabel } from "../../CommonUI/BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export const ToolboxDiv = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    padding-right: 10px;
    border-radius: 0;
    height: 100%;
    flex-basis: 50px;

    @media (min-width: 1366px) {
        flex-basis: calc(236px + 59 * (100vw - 1366px) / 554);
    }

    @media (min-width: 1920px) {
        flex-basis: 295px;
    }
`;

const ToolboxContent = styled.div`
    border-top: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    flex: 1;
    overflow: auto;
    padding-left: 12px;
    padding-right: 10px;
    padding-top: 10px;
`;

const StyledAccordion = styled(Accordion)`
    && {
        border: none;
        box-shadow: none;
    }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
    && {
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        display: none;
        padding: 0;
        min-height: 0 !important;

        @media (min-width: 1366px) {
            display: flex;
            margin-bottom: 13px;
            line-height: 23px;
        }
        @media (min-width: 1920px) {
            margin-bottom: 19px;
            line-height: 25px;
        }

        .MuiAccordionSummary-content {
            margin: 0;
        }
    }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
    && {
        display: flex;
        flex-direction: column;
        padding: 0;
    }
`;

interface IToolBoxPropsInterface {
    onSelectFormDefaultConfig: CallableFunction;
    mode: FormMode | string;
}

export class ToolBox extends React.Component<IToolBoxPropsInterface> {
    constructor(props: IToolBoxPropsInterface) {
        super(props, {});
        window.addEventListener("resize", () => this.forceUpdate());
    }

    render() {
        return (
            <Droppable
                isDropDisabled={true}
                droppableId={"toolbox"}
            >
                {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
                    return (
                        <ToolboxDiv
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            <Box
                                display={"flex"}
                                align-items={"center"}
                                borderBottom={"1px"}
                                borderColor={UIStyleConstants.GRAY_VERY_LIGHT_COLOR}
                                paddingLeft="12px"
                                paddingTop="10px"
                                paddingBottom="10px"
                                height="50px"
                            >
                                <UIButton
                                    variant="contained"
                                    onClick={() => this.props.onSelectFormDefaultConfig()}
                                    data-testid="edit-form-defaults"
                                >
                                    Edit Form Settings
                                </UIButton>
                            </Box>
                            <ToolboxContent>
                                {Object.entries(
                                    Object.entries(UIControlRegistration).reduce(
                                        (
                                            obj: {
                                                [key: string]: {
                                                    name: string;
                                                    data: RegistrationData;
                                                }[];
                                            },
                                            [name, data],
                                        ) => {
                                            if (!obj[data.category]) {
                                                obj[data.category] = [];
                                            }
                                            obj[data.category].push({ name, data });
                                            return obj;
                                        },
                                        {},
                                    ),
                                ).map(([category, controls]) => {
                                    const doms = controls
                                        .map((control, index: number) => {
                                            const data = control.data;
                                            const name = control.name;
                                            if (
                                                this.props.mode === FormMode.LayoutDesign &&
                                                data.typeOfControl === ControlType.Control
                                            ) {
                                                return null;
                                            }
                                            if (
                                                this.props.mode === FormMode.FormDesign &&
                                                data.typeOfControl === ControlType.Container
                                            ) {
                                                return null;
                                            }

                                            // this code does not work.  it is the code to leave an item in the toolbox when dragging
                                            const shouldRenderClone = name === snapshot.draggingFromThisWith;
                                            return (
                                                <React.Fragment key={name}>
                                                    {shouldRenderClone && (
                                                        <Box
                                                            border={"1px solid " + UIStyleConstants.GRAY_LIGHTER_COLOR}
                                                            borderRadius="5pxpx"
                                                            display={"flex"}
                                                            flexDirection={"row"}
                                                            padding={"5px"}
                                                            paddingTop={"15px"}
                                                            paddingBottom={"15px"}
                                                            alignItems={"center"}
                                                            justifyContent={"left"}
                                                            height={"28px"}
                                                            width={"100%"}
                                                            marginBottom={"10px"}
                                                            style={{
                                                                borderColor:
                                                                    FormDesignConstants.TOOLBOX_LEFT_BEHIND_COLOR,
                                                            }}
                                                        >
                                                            <Box width={"20px"}>{data.icon}</Box>
                                                            <UILabel
                                                                variant={"small"}
                                                                paddingLeft={"10px"}
                                                                noWrap={true}
                                                            >
                                                                {data.name}
                                                            </UILabel>
                                                        </Box>
                                                    )}
                                                    <Draggable
                                                        draggableId={name}
                                                        index={index}
                                                    >
                                                        {(
                                                            provided: DraggableProvided,
                                                            snapshot: DraggableStateSnapshot,
                                                        ) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                            >
                                                                {
                                                                    <Tooltip
                                                                        title={data.name}
                                                                        placement="top"
                                                                        arrow
                                                                    >
                                                                        <Box
                                                                            border={
                                                                                "1px solid " +
                                                                                UIStyleConstants.GRAY_LIGHTER_COLOR
                                                                            }
                                                                            borderRadius="5pxpx"
                                                                            display={"flex"}
                                                                            flexDirection={"row"}
                                                                            padding={"5px"}
                                                                            paddingTop={"15px"}
                                                                            paddingBottom={"15px"}
                                                                            alignItems={"center"}
                                                                            justifyContent={"left"}
                                                                            height={"28px"}
                                                                            width={"100%"}
                                                                            marginBottom={"10px"}
                                                                            style={{
                                                                                borderColor: snapshot.isDragging
                                                                                    ? FormDesignConstants.TOOLBOX_DRAGGING_COLOR
                                                                                    : FormDesignConstants.TOOLBOX_NOT_DRAGGING_COLOR,
                                                                            }}
                                                                        >
                                                                            <Box width={"20px"}>{data.icon}</Box>
                                                                            <UILabel
                                                                                variant={"small"}
                                                                                noWrap={true}
                                                                                paddingLeft={"10px"}
                                                                            >
                                                                                {data.name}
                                                                            </UILabel>
                                                                        </Box>
                                                                    </Tooltip>
                                                                }
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                </React.Fragment>
                                            );
                                        })
                                        .filter((d) => !!d);

                                    if (doms.length === 0) return null;

                                    // todo: make this a method on control registration

                                    return (
                                        <StyledAccordion
                                            defaultExpanded
                                            key={category}
                                        >
                                            <StyledAccordionSummary expandIcon={<ExpandIcon />}>
                                                {category}
                                            </StyledAccordionSummary>
                                            <StyledAccordionDetails>{doms}</StyledAccordionDetails>
                                        </StyledAccordion>
                                    );
                                })}
                            </ToolboxContent>
                            {provided.placeholder}
                        </ToolboxDiv>
                    );
                }}
            </Droppable>
        );
    }
}
